import Fuse from 'fuse.js';
import React, { useMemo } from 'react';

import type * as api from '@gitbook/api-client';
import { assertNever, filterOutNullable } from '@gitbook/util-ts';

import { NavigationRedirect, type NavigationTarget } from '../../navigation';
import {
    excludeSiteExternalLinks,
    flattenGroupedSections,
    getSiteSectionGroupChildren,
    getSiteSpacesFromStructure,
} from '../../state';
import { buildFuseSearchQuery } from '../../utils';
import { useContentReferenceContext } from '../ContentReferences';

/**
 * Get the sites of an organization.
 * It's designed to match the `listSites` API, while using the optimized `getOrganizationAllContent` API that is prefetched by the sidebar.
 */
export function useOrganizationSites(
    data: api.OrganizationAllContent | null,
    filter: {
        /** Filter by published status */
        published?: boolean;

        /** Filter by title */
        title?: string;

        /** Filter by space */
        spaceId?: string;
    } = {}
): api.OrganizationAllSite[] {
    return React.useMemo(() => {
        if (!data) {
            return [];
        }

        const allSites = data.sites;

        return allSites.filter((item) => {
            if (filter.published !== undefined && item.site.published !== filter.published) {
                return false;
            }

            if (
                filter.title &&
                !item.site.title.toLowerCase().includes(filter.title.toLowerCase())
            ) {
                return false;
            }

            if (filter.spaceId) {
                return Boolean(getSpaceInSiteStructure(item.structure, filter.spaceId));
            }

            return true;
        });
    }, [data, filter.published, filter.title, filter.spaceId]);
}

/**
 * Get the ancestors of a content in an organization.
 */
export function useOrganizationContentItem(
    data: api.OrganizationAllContent,
    content: { collection: string; space?: undefined }
): OrgAllContentItem<api.OrganizationAllCollection> | undefined;
export function useOrganizationContentItem(
    data: api.OrganizationAllContent,
    content: { space: string; collection?: undefined }
): OrgAllContentItem<api.OrganizationAllSpace> | undefined;
export function useOrganizationContentItem(
    data: api.OrganizationAllContent,
    content: { collection: string; space?: undefined } | { space: string; collection?: undefined }
): OrgAllContentItem<api.OrganizationAllCollection | api.OrganizationAllSpace> | undefined {
    return React.useMemo(() => {
        if (content.collection) {
            return getCollectionFromOrgAllContent(data, content.collection);
        }

        if (content.space) {
            return getSpaceFromOrgAllContent(data, content.space);
        }

        return;
    }, [data, content.collection, content.space]);
}

/**
 * Get all the sites linked to a space.
 */
export function useSpaceSites(
    organizationContent: api.OrganizationAllContent | null,
    spaceId: string
) {
    const sites = useOrganizationSites(organizationContent, { spaceId });

    return React.useMemo(() => {
        return sites
            .map((entry) => {
                const siteSpaceLocation = getSpaceLocationInSiteStructure(entry.structure, spaceId);
                if (!siteSpaceLocation) {
                    return null;
                }

                return {
                    site: entry.site,
                    ...siteSpaceLocation,
                };
            })
            .filter(filterOutNullable);
    }, [sites, spaceId]);
}

export interface SpaceSiteLookup {
    site: api.Site;
    siteSpace: api.SiteSpace;
    /** Section containing the site space when the site uses sections. */
    section?: api.SiteSection | null;
    /** Section groups containing the section, ordered from root to direct parent. */
    sectionGroups?: api.SiteSectionGroup[];
}

const SpacePrimarySiteReactContext = React.createContext<SpaceSiteLookup | null | undefined>(
    undefined
);

/**
 * Resolve the primary site linked to a space from a list of linked sites.
 */
export function getPrimarySiteForSpace(
    sites: SpaceSiteLookup[],
    preferredSiteId?: string | null
): SpaceSiteLookup | null {
    if (preferredSiteId) {
        const preferred = sites.find((s) => s.site.id === preferredSiteId);
        if (preferred) return preferred;
    }

    return (
        sites.find((s) => s.site.published) ??
        sites.find((s) => s.siteSpace.default === true) ??
        sites[0] ??
        null
    );
}

/**
 * Provide the primary site linked to a space.
 *
 * Reads `space` and `site` from the surrounding `ContentReferenceContext`. When
 * When there's no `site` in context, the Provider redirects to the resolved primary site so the
 * URL gains a site segment.
 */
export function SpacePrimarySiteProvider(props: {
    organizationContent: api.OrganizationAllContent | null;
    children: React.ReactNode;
}) {
    const { organizationContent, children } = props;
    const contentReferenceContext = useContentReferenceContext();
    const spaceId = contentReferenceContext.space;
    const siteId = contentReferenceContext.site;

    if (!spaceId) {
        throw new Error('SpacePrimarySiteProvider expects a space in ContentReferenceContext');
    }

    const sites = useSpaceSites(organizationContent, spaceId);
    const primarySite = useMemo(() => getPrimarySiteForSpace(sites, siteId), [sites, siteId]);

    if (!siteId && primarySite) {
        return <NavigationRedirect to={{ current: { site: primarySite.site.id } }} replace />;
    }

    return (
        <SpacePrimarySiteReactContext.Provider value={primarySite}>
            {children}
        </SpacePrimarySiteReactContext.Provider>
    );
}

/**
 * Get the primary site linked to the current space.
 */
export function useSpacePrimarySite(): SpaceSiteLookup | null {
    const primarySite = React.useContext(SpacePrimarySiteReactContext);

    if (primarySite === undefined) {
        throw new Error('useSpacePrimarySite expects a SpacePrimarySiteProvider above it');
    }

    return primarySite;
}

/**
 * Flatten the list of spaces in the content of an organization.
 */
export function flattenSpacesInOrgAllContent<T = api.Space>(
    allContent: api.OrganizationAllContent,
    map: (space: api.OrganizationAllSpace) => T | null = (item) => item.space as T
): T[] {
    const iterate = (
        items: Array<api.OrganizationAllCollection | api.OrganizationAllSpace>
    ): T[] => {
        const spaces: T[] = [];
        for (const item of items) {
            if ('space' in item) {
                const mapped = map(item);
                if (mapped) {
                    spaces.push(mapped);
                }
            } else {
                spaces.push(...iterate(item.content));
            }
        }

        return spaces;
    };

    return iterate(allContent.spaces);
}

/**
 * Get a site item from the OrganizationAllContent.
 */
export function getSiteFromOrgAllContent(
    allContent: api.OrganizationAllContent,
    siteId: string
): OrgAllContentItem<api.OrganizationAllSite> | undefined {
    const { sites } = getIndexesFromOrgAllContent(allContent);
    return sites.get(siteId);
}

/** Get the appropriate site entry point for the user's permission level. */
export function getSiteNavigationTarget(input: {
    organizationId: string;
    siteContent: api.OrganizationAllSite;
}): NavigationTarget {
    const { organizationId, siteContent } = input;
    const { site, structure } = siteContent;

    // Users with space-only access are directed to the site's space screen.
    if (!site.permissions.access) {
        const firstAccessibleSiteSpace = getSiteSpacesFromStructure(structure)[0];
        if (firstAccessibleSiteSpace) {
            return {
                screen: {
                    type: 'space',
                    organization: organizationId,
                    site: site.id,
                    space: firstAccessibleSiteSpace.space.id,
                    page: '',
                },
            };
        }
    }

    // Users with site access are directed to the site's root screen.
    return {
        screen: {
            type: 'site',
            organization: organizationId,
            site: site.id,
        },
    };
}

/**
 * Get a collection item from the OrganizationAllContent.
 */
export function getCollectionFromOrgAllContent(
    allContent: api.OrganizationAllContent,
    collectionId: string
): OrgAllContentItem<api.OrganizationAllCollection> | undefined {
    const { collections } = getIndexesFromOrgAllContent(allContent);
    return collections.get(collectionId);
}

/**
 * Get a space item from the OrganizationAllContent.
 */
export function getSpaceFromOrgAllContent(
    allContent: api.OrganizationAllContent,
    spaceId: string
): OrgAllContentItem<api.OrganizationAllSpace> | undefined {
    const { spaces } = getIndexesFromOrgAllContent(allContent);
    return spaces.get(spaceId);
}

/**
 * Get a site space from a structure by its space ID.
 */
export function getSpaceInSiteStructure(
    structure: api.SiteStructure,
    spaceId: string
): api.SiteSpace | undefined {
    if (structure.type === 'siteSpaces') {
        return structure.structure.find((siteSpace) => siteSpace.space.id === spaceId);
    }

    for (const section of flattenGroupedSections(structure.structure)) {
        const result = section.siteSpaces.find((siteSpace) => siteSpace.space.id === spaceId);
        if (result) {
            return result;
        }
    }

    return;
}

/**
 * Resolve sites linked to every space in an organization.
 */
export interface SpaceSitesLookup {
    space: api.Space;
    sites: SpaceSiteLookup[];
}

export function getOrganizationSpacesWithSites(
    organizationContent: api.OrganizationAllContent
): SpaceSitesLookup[] {
    const sitesBySpaceId = new Map<string, SpaceSiteLookup[]>();

    for (const { site, structure } of organizationContent.sites) {
        for (const { siteSpace, section, sectionGroups } of getSiteSpaceLocationsFromSiteStructure(
            structure
        )) {
            const sites = sitesBySpaceId.get(siteSpace.space.id) ?? [];
            sites.push({ site, siteSpace, section, sectionGroups });
            sitesBySpaceId.set(siteSpace.space.id, sites);
        }
    }

    return Array.from(getIndexesFromOrgAllContent(organizationContent).spaces.values()).map(
        ({ item }) => ({
            space: item.space,
            sites: sitesBySpaceId.get(item.space.id) ?? [],
        })
    );
}

export interface SiteSpaceLocation {
    siteSpace: api.SiteSpace;
    section: api.SiteSection | null;
    sectionGroups: api.SiteSectionGroup[];
}

/**
 * Get a site space and its site-structure ancestors from a structure by its space ID.
 */
export function getSpaceLocationInSiteStructure(
    structure: api.SiteStructure,
    spaceId: string
): SiteSpaceLocation | undefined {
    return getSiteSpaceLocationsFromSiteStructure(structure).find(
        ({ siteSpace }) => siteSpace.space.id === spaceId
    );
}

/**
 * Get all site spaces with their site-structure ancestors from a site structure.
 */
export function getSiteSpaceLocationsFromSiteStructure(
    structure: api.SiteStructure
): SiteSpaceLocation[] {
    switch (structure.type) {
        case 'siteSpaces':
            return structure.structure.map((siteSpace) => ({
                siteSpace,
                section: null,
                sectionGroups: [],
            }));
        case 'sections':
            return excludeSiteExternalLinks(structure.structure).flatMap((item) =>
                getSiteSpaceLocationsFromSectionItem(item, [])
            );
        default:
            assertNever(structure);
    }
}

function getSiteSpaceLocationsFromSectionItem(
    item: api.SiteSection | api.SiteSectionGroup | api.SiteExternalLink,
    sectionGroups: api.SiteSectionGroup[]
): SiteSpaceLocation[] {
    switch (item.object) {
        case 'site-section':
            return item.siteSpaces.map((siteSpace) => ({
                siteSpace: {
                    ...siteSpace,
                    section: item.id,
                },
                section: item,
                sectionGroups,
            }));
        case 'site-section-group':
            return getSiteSectionGroupChildren(item).flatMap((child) =>
                getSiteSpaceLocationsFromSectionItem(child, [...sectionGroups, item])
            );
        case 'site-external-link':
            return [];
        default:
            assertNever(item);
    }
}

/**
 * Get a site section from a structure by its ID.
 */
export function getSiteSectionInSiteStructure(
    structure: api.SiteStructure,
    sectionId: string
): api.SiteSection | null {
    switch (structure.type) {
        case 'siteSpaces':
            return null;
        case 'sections':
            return (
                flattenGroupedSections(structure.structure).find(
                    (section) => section.id === sectionId
                ) ?? null
            );
        default:
            assertNever(structure);
    }
}

/**
 * Get the siblings site spaces of a site space in a structure.
 */
export function getSiteSpaceSiblingsInSiteStructure(
    structure: api.SiteStructure,
    siteSpaceId: string
): api.SiteSpace[] {
    const siteSpaces = (() => {
        if (structure.type === 'siteSpaces') {
            return structure.structure;
        }

        const section = flattenGroupedSections(structure.structure).find((section) =>
            section.siteSpaces.some((siteSpace) => siteSpace.id === siteSpaceId)
        );
        return section?.siteSpaces ?? [];
    })();

    return siteSpaces.filter((siteSpace) => siteSpace.id !== siteSpaceId);
}

/**
 * Get a site space from a structure by its ID.
 */
export function getSiteSpaceInSiteStructure(
    structure: api.SiteStructure,
    siteSpaceId: string
): api.SiteSpace | undefined {
    if (structure.type === 'siteSpaces') {
        return structure.structure.find((siteSpace) => siteSpace.id === siteSpaceId);
    }

    for (const section of flattenGroupedSections(structure.structure)) {
        const result = section.siteSpaces.find((siteSpace) => siteSpace.id === siteSpaceId);
        if (result) {
            return result;
        }
    }

    return;
}

/**
 * Get the section that contains a site space in a sections-based structure.
 */
export function getSiteSectionForSiteSpace(
    structure: api.SiteStructure,
    siteSpaceId: string
): api.SiteSection | undefined {
    if (structure.type !== 'sections') {
        return;
    }

    return flattenGroupedSections(structure.structure).find((section) =>
        section.siteSpaces.some((siteSpace) => siteSpace.id === siteSpaceId)
    );
}

/**
 * Get the default site space for a site.
 */
export function getDefaultSiteSpaceInSiteStructure(
    structure: api.SiteStructure
): api.SiteSpace | undefined {
    if (structure.type === 'siteSpaces') {
        return structure.structure.find((siteSpace) => siteSpace.default);
    }

    for (const section of flattenGroupedSections(structure.structure)) {
        if (section.default) {
            const result = section.siteSpaces.find((siteSpace) => siteSpace.default);
            if (result) {
                return result;
            }
        }
    }

    return;
}

export type OrgAllContentItem<T> = { item: T; ancestors: api.OrganizationAllCollection[] };
export type OrgAllContentItemsIndex<T> = Map<string, OrgAllContentItem<T>>;

const cache = new WeakMap<api.OrganizationAllContent, ReturnType<typeof indexContentItems>>();

/**
 * Compute and cache indexes of spaces/collections from an OrganizationAllContent.
 */
export function getIndexesFromOrgAllContent(content: api.OrganizationAllContent) {
    const existing = cache.get(content);

    if (existing) {
        return existing;
    }

    const indexes = indexContentItems(content);
    cache.set(content, indexes);
    return indexes;
}

function indexContentItems(content: api.OrganizationAllContent) {
    const sites: OrgAllContentItemsIndex<api.OrganizationAllSite> = new Map();
    const spaces: OrgAllContentItemsIndex<api.OrganizationAllSpace> = new Map();
    const collections: OrgAllContentItemsIndex<api.OrganizationAllCollection> = new Map();

    const indexLevel = (
        items: Array<
            api.OrganizationAllSite | api.OrganizationAllCollection | api.OrganizationAllSpace
        >,
        ancestors: api.OrganizationAllCollection[] = []
    ) => {
        for (const item of items) {
            if ('site' in item) {
                sites.set(item.site.id, { item, ancestors });
            } else if ('space' in item) {
                spaces.set(item.space.id, { item, ancestors });
            } else {
                collections.set(item.collection.id, { item, ancestors });
                indexLevel(item.content, [...ancestors, item]);
            }
        }
    };

    indexLevel(content.sites);
    indexLevel(content.spaces);

    return { sites, spaces, collections };
}

/**
 * Filter organization content items by a search string using Fuse.js.
 * Operates on already-fetched data — no additional API call.
 */
export type OrganizationContentItem = api.OrganizationAllCollection | api.OrganizationAllSpace;

/**
 * Search organization content and return both the filtered tree and matching ids.
 */
export function searchOrganizationContent(
    data: api.OrganizationAllContent,
    search: string
): {
    items: OrganizationContentItem[];
    matchingIds: Set<string> | null;
} {
    const matchingIds = getSearchOrganizationContentItemIds(data, search);
    if (matchingIds === null) {
        return {
            items: data.spaces,
            matchingIds: null,
        };
    }

    return {
        items: filterOrganizationContentItems(data.spaces, matchingIds),
        matchingIds,
    };
}

const organizationContentFuseCache = new WeakMap<
    api.OrganizationAllContent,
    Fuse<{ id: string; title: string }>
>();

function getOrganizationContentFuse(orgAllContent: api.OrganizationAllContent) {
    const cachedOrganizationContentFuse = organizationContentFuseCache.get(orgAllContent);
    if (cachedOrganizationContentFuse) {
        return cachedOrganizationContentFuse;
    }

    const searchableItems = flattenOrganizationContentItems(orgAllContent.spaces);
    const fuse = new Fuse(searchableItems, {
        shouldSort: true,
        threshold: 0.35,
        ignoreLocation: true,
        minMatchCharLength: 2,
        useExtendedSearch: true,
        keys: ['title'],
    });

    organizationContentFuseCache.set(orgAllContent, fuse);
    return fuse;
}

function flattenOrganizationContentItems(
    items: OrganizationContentItem[]
): Array<{ id: string; title: string }> {
    return items.flatMap((item) => {
        if ('space' in item) {
            return [{ id: item.space.id, title: item.space.title }];
        }

        return [
            { id: item.collection.id, title: item.collection.title },
            ...flattenOrganizationContentItems(item.content),
        ];
    });
}

function filterOrganizationContentItems(
    items: OrganizationContentItem[],
    matchingIds: Set<string>
): OrganizationContentItem[] {
    return items.reduce<OrganizationContentItem[]>((filtered, item) => {
        if ('space' in item) {
            if (matchingIds.has(item.space.id)) {
                filtered.push(item);
            }

            return filtered;
        }

        const content = filterOrganizationContentItems(item.content, matchingIds);
        if (matchingIds.has(item.collection.id) || content.length > 0) {
            filtered.push({
                ...item,
                content,
            });
        }

        return filtered;
    }, []);
}

function getSearchOrganizationContentItemIds(
    data: api.OrganizationAllContent,
    search: string
): Set<string> | null {
    const trimmed = search.trim();
    if (!trimmed) {
        return null;
    }

    const queryTokens = trimmed.split(/\s+/).filter(Boolean);
    const fuseQuery = buildFuseSearchQuery(queryTokens, ['title'], { minTokenLength: 2 });

    return new Set(
        getOrganizationContentFuse(data)
            .search(fuseQuery)
            .map((result) => result.item.id)
    );
}
